import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {BasicPage} from '../components/basic-page';
import {ROUTES} from '../routes';
import {FC, useMemo} from 'react';
import {BreadcrumbPart} from '../components/page-title';
import {useAsync} from 'react-use';
import {Divider, Grid, Stack, Text} from '@mantine/core';
import {EmailLogsService} from '../api/generated/EmailLogsService';
import {css} from '@emotion/react';
import {AsyncStateContainer} from '../components/async-state-container';
import {DateTime} from 'luxon';
import {DateFormat} from '../components/date';
import {FilterOnOptions} from '../api/generated/enums';

export const EmailLogView = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.EMAIL_LOG.DETAIL);

  const {value, loading} = useAsync(async () => {
    return await EmailLogsService.getById({id});
  }, [id]);

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Email Logs', href: ROUTES.ADMIN.EMAIL_LOG.INDEX.path},
      {title: `${id}`},
    ],
    [id],
  );

  return (
    <AsyncStateContainer loading={loading}>
      <BasicPage title={breadcrumbs}>
        <Divider mt={12} mb={38} />
        <Grid bd='2px solid black'>
          <Grid.Col span={3} css={leftColCss} p={0} bg='white'>
            <ColStack>
              <TextLabel>Email Sent On</TextLabel>
              <RowDivider />
              <TextLabel>Status</TextLabel>
              <RowDivider />
              <TextLabel>Job ID</TextLabel>
              <RowDivider />
              <TextLabel>Serial Number</TextLabel>
              <RowDivider />
              <TextLabel>Client Name</TextLabel>
              <RowDivider />
              <TextLabel>Job Created By</TextLabel>
              <RowDivider />
              <TextLabel>Can be Repaired</TextLabel>
              <RowDivider />
              <TextLabel>Level of Service</TextLabel>
              <RowDivider />
              <TextLabel>Email Recipients</TextLabel>
              <RowDivider />
            </ColStack>
          </Grid.Col>
          <Grid.Col span={9} css={rightColCss} p={0} bg='white'>
            <ColStack>
              <TextValue>
                {value?.data?.createdDate && (
                  <DateFormat
                    date={value.data.createdDate}
                    format={DateTime.DATE_SHORT}
                    lh='24px'
                  />
                )}
              </TextValue>
              <RowDivider />
              <TextValue>{value?.data?.status}</TextValue>
              <RowDivider />
              <TextValue>{value?.data?.jobId}</TextValue>
              <RowDivider />
              <TextValue>{value?.data?.jobSerialNumber}</TextValue>
              <RowDivider />
              <TextValue>{value?.data?.jobClientName}</TextValue>
              <RowDivider />
              <TextValue>{`${value?.data?.jobCreatedByUserFirstName ?? ''} ${value?.data?.jobCreatedByUserLastName ?? ''}`}</TextValue>
              <RowDivider />
              <TextValue>
                {value?.data?.jobEvaluationIsRepairable === true
                  ? FilterOnOptions.Yes
                  : value?.data?.jobEvaluationIsRepairable === false
                    ? FilterOnOptions.No
                    : FilterOnOptions.Unknown}
              </TextValue>
              <RowDivider />
              <TextValue>{value?.data?.jobLevelOfServiceDescription}</TextValue>
              <RowDivider />
              <TextValue>{value?.data?.emailRecipients}</TextValue>
            </ColStack>
          </Grid.Col>
        </Grid>
      </BasicPage>
    </AsyncStateContainer>
  );
};

type Parent = {
  children?: React.ReactNode;
};

const TextLabel: FC<Parent> = ({children}) => (
  <Text ta='center' h={44} fw='bold' fz={14} p='xs' m='auto auto auto 12'>
    {children}
  </Text>
);

const TextValue: FC<Parent> = ({children}) => (
  <Text ta='center' h={44} fz={14} p='xs' m='auto auto auto 12'>
    {children}
  </Text>
);

const RowDivider: FC = () => <Divider size={2} color='rgba(0, 0, 0, 0.05)' />;

const ColStack: FC<Parent> = ({children}) => (
  <Stack justify='center' h='100%' gap={0}>
    {children}
  </Stack>
);

const leftColCss = css({
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderRight: '0.5px solid rgba(0, 0, 0, 0.075)',
  borderRadius: '6px 0 0 6px',
});

const rightColCss = css({
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderLeft: '0.5px solid rgba(0, 0, 0, 0.075)',
  borderRadius: '0 6px 6px 0',
});
